import React from 'react';

import { Box, Button, ButtonGroup, Flex, Tr, Td, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { Icon } from '@firehydrant/design-system';
import FormV2 from 'components/common/Form/FormV2/FormV2';

import {
  getRowComplianceStatus,
  formatDelay,
} from './helpers/complianceHelpers';
import { timeOptions } from './helpers/signalsNotificationFormHelpers';
import { NotificationIcon } from './NotificationIcon';
import { PolicyComplianceIndicator } from './NotificationPolicy/PolicyComplianceIndicator';
import { NotificationLabel } from './SignalsNotificationLabel';

export const NotificationEditRow = ({
  index,
  notificationOptions,
  item,
  formik,
  cancelEdit,
  delayOptions = timeOptions,
  editDestination = false,
  showPolicyColumns = true,
  complianceStatus,
}) => {

  return (
    <Tr>
      {/* Type column */}
      <Td maxWidth={{ base: '120px', md: 'none' }}>
        {editDestination || !item.id ? (
          <FormV2.Dropdown
            name={`notifications[${index}].notification_type`}
            arrayItemIndex={index}
            options={notificationOptions}
            placeholder="Select..."
            data-testid="notification-type"
            mb="0"
            size="sm"
            menuPortalTarget={document.querySelector(
              `#notification-form-${index}`,
            )}
            menuPosition="fixed"
            fitContent
          />
        ) : (
          <Flex alignItems="center" gap="2" overflow="hidden">
            <NotificationIcon
              type={item.notification_type?.notification_type}
            />
            <Box isTruncated title={item.notification_type?.notification_type}>
              <NotificationLabel notification={item} />
            </Box>
          </Flex>
        )}
      </Td>

      {/* Delay column */}
      <Td>
        <FormV2.Dropdown
          name={`notifications[${index}].delay`}
          arrayItemIndex={index}
          options={delayOptions}
          placeholder="Select Delay"
          data-testid="delay"
          mb="0"
          menuPortalTarget={document.querySelector(
            `#notification-form-${index}`,
          )}
          menuPosition="fixed"
          size="sm"
          fitContent
        />
      </Td>

      {/* In Policy column - only show if showPolicyColumns is true */}
      {showPolicyColumns && (
        <Td width={{ base: '60px', md: 'auto' }} textAlign="center">
          <PolicyComplianceIndicator
            isCompliant={complianceStatus.isCompliant}
            size="sm"
            showLabel={false}
            userId=""
          />
        </Td>
      )}

      {/* Policy column - only show if showPolicyColumns is true */}
      {showPolicyColumns && (
        <Td display={{ base: 'none', md: 'table-cell' }}>
          {complianceStatus.isCompliant === false ? (
            <Text textStyle="body1" color="content.feedback.error.secondary">
              {complianceStatus.requiredDelay ? (
                <>Required: {formatDelay(complianceStatus.requiredDelay)}</>
              ) : (
                'Non-compliant'
              )}
            </Text>
          ) : complianceStatus.isCompliant === true ? (
            <Text textStyle="body1" color="content.feedback.success.secondary">
              Compliant
            </Text>
          ) : (
            <Text textStyle="body1" color="content.secondary">
              No policy
            </Text>
          )}
        </Td>
      )}

      {/* Actions column */}
      <Td>
        <ButtonGroup>
          <Button type="submit" size="sm">
            Save
          </Button>
          <Button
            onClick={() => cancelEdit(formik.resetForm)}
            size="sm"
            variant="ghost"
            leftIcon={<Icon name="close" boxSize="3" />}
          >
            Discard
          </Button>
        </ButtonGroup>
        <Box display="none">
          <FormV2.Input name={`notifications[${index}].id`} />
        </Box>
      </Td>
    </Tr>
  );
};

NotificationEditRow.propTypes = {
  index: PropTypes.number.isRequired,
  notificationOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  item: PropTypes.shape({
    notification_type: PropTypes.shape({
      notification_type: PropTypes.string,
    }),
    delay: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  }).isRequired,
  formik: PropTypes.object.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  editDestination: PropTypes.bool,
  delayOptions: PropTypes.arrayOf(PropTypes.object),
  policyItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      notification_group_method: PropTypes.string,
      max_delay: PropTypes.string,
      priority: PropTypes.string,
    }),
  ),
  notificationPriority: PropTypes.string,
  showPolicyColumns: PropTypes.bool,
};
